import React, { useEffect, useRef } from 'react';

import { RealtimeCallback } from 'shared/lib/realtime';
import { StreamContent } from 'widgets/stream-preview';
import { useStreamContentData } from 'widgets/stream-preview/lib';
import { useRealtimeAssignments } from 'entities/assignments';
import { useInfiniteScroll } from 'shared/lib/utils';
import { NoItemsError } from 'shared/ui/errors';
import { Spinner } from 'shared/ui/spinner';

import { StreamPageLayout } from './stream-page-layout';

const StreamPage = () => {
  const {
    itemsToDisplay,
    isLoading: isContentLoading,
    isFetchingOnClassChange,
    incrementLimitOffset,
    isFetching,
    isAllTaughtLessonsFetched,
    isAllAssignmentsFetched,
    selectedSubjects,
    refetchAssignments,
    currentClassesKeys,
  } = useStreamContentData(true);

  const { isLoadMoreRefVisible, setLoadMoreRef } = useInfiniteScroll();
  const preventInitialIncrement = useRef(true);

  const noItemsToDisplayReceived =
    !isContentLoading && !!itemsToDisplay && !itemsToDisplay.length;

  // When no items to display received and all items are fetched from the api already.
  const shouldShowNoItemsAllPrograms =
    noItemsToDisplayReceived &&
    isAllTaughtLessonsFetched &&
    isAllAssignmentsFetched;

  // When there are no items for selected program.
  const showNoItemsForSelectedProgram =
    noItemsToDisplayReceived && !!selectedSubjects.length;

  const shouldShowSpinner = isContentLoading || isFetchingOnClassChange;
  const shouldShowNoItems =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    shouldShowNoItemsAllPrograms || showNoItemsForSelectedProgram;

  // Increment offset/limit query parameters if 'load more' ref visible and data isn't initially fetching.
  useEffect(() => {
    if (!isLoadMoreRefVisible) return;

    if (!preventInitialIncrement.current) {
      incrementLimitOffset();
    } else {
      preventInitialIncrement.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadMoreRefVisible]);

  const handleAssignmentsUpdatesRealtime: RealtimeCallback = (_, payload) => {
    const { isFromEducatorHome } = payload;

    // do not re-fetch assignments if realtime update is coming from Educator Home
    // as it will be done via query invalidation after PUT or DELETE request
    if (isFromEducatorHome) return;

    refetchAssignments();
  };

  useRealtimeAssignments({
    assignmentAssignedCallback: refetchAssignments,
    assignmentUpdatedCallback: handleAssignmentsUpdatesRealtime,
    classesKeys: currentClassesKeys,
  });

  if (shouldShowSpinner) {
    return (
      <StreamPageLayout>
        <Spinner
          isDark
          isLarge
          classNames="rhythm-mt-5"
          testId="initial-stream-spinner"
        />
        <div className="position-relative" ref={setLoadMoreRef} />
      </StreamPageLayout>
    );
  }

  if (shouldShowNoItems) {
    return (
      <StreamPageLayout>
        <NoItemsError />
      </StreamPageLayout>
    );
  }

  return (
    <StreamPageLayout>
      <StreamContent items={itemsToDisplay} />
      <div className="position-relative" ref={setLoadMoreRef}>
        {isFetching && (
          <Spinner
            isDark
            isLarge
            classNames="rhythm-mt-2"
            data-testid="stream-spinner"
          />
        )}
      </div>
    </StreamPageLayout>
  );
};

export default StreamPage;
